import { useEffect } from 'react';

const SITE_ORIGIN = 'https://nextraine.com';

/**
 * Custom hook to dynamically update document title and meta description.
 * This is crucial for SPA SEO and web crawlers that inspect page-specific metadata.
 *
 * Also keeps the canonical link and og:url in sync with the current path, and —
 * for pages that opt out of indexing (see the /intestazione letterhead page) —
 * sets a noindex robots meta tag.
 */
export const useDocumentMetadata = (title: string, description?: string, noindex = false) => {
  useEffect(() => {
    // 1. Update Title
    const prevTitle = document.title;
    document.title = title;

    // Helper to find or create meta tag
    const updateMetaTag = (selector: string, attrName: string, attrValue: string, content: string) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // 2. Update OG Title
    updateMetaTag('meta[property="og:title"]', 'property', 'og:title', title);
    updateMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', title);

    // 3. Update Meta Description and OG Description
    if (description) {
      updateMetaTag('meta[name="description"]', 'name', 'description', description);
      updateMetaTag('meta[property="og:description"]', 'property', 'og:description', description);
      updateMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }

    // 4. Canonical URL + og:url, derived from the current path
    const canonicalUrl = `${SITE_ORIGIN}${window.location.pathname}`;
    let canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonicalUrl);
    updateMetaTag('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);

    // 5. Robots directive — only pages that explicitly opt out set this
    let robotsEl = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (noindex) {
      if (!robotsEl) {
        robotsEl = document.createElement('meta');
        robotsEl.setAttribute('name', 'robots');
        document.head.appendChild(robotsEl);
      }
      robotsEl.setAttribute('content', 'noindex, nofollow');
    } else if (robotsEl) {
      robotsEl.remove();
    }

    // 6. hreflang alternates (it/en/x-default) — tells Google these are the same
    // page in two languages rather than duplicate content. Skipped for noindex
    // pages (e.g. the letterhead), which have no translated counterpart anyway.
    const setHreflang = (lang: string, href: string) => {
      const selector = `link[rel="alternate"][hreflang="${lang}"]`;
      let el = document.querySelector<HTMLLinkElement>(selector);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', lang);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };
    const removeHreflang = (lang: string) => {
      document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`)?.remove();
    };

    if (noindex) {
      ['it', 'en', 'x-default'].forEach(removeHreflang);
    } else {
      const rawPath = window.location.pathname;
      const isEnPath = rawPath === '/en' || rawPath.startsWith('/en/');
      const itPath = isEnPath ? rawPath.replace(/^\/en/, '') || '/' : rawPath;
      const enPath = isEnPath ? rawPath : rawPath === '/' ? '/en' : `/en${rawPath}`;
      setHreflang('it', `${SITE_ORIGIN}${itPath}`);
      setHreflang('en', `${SITE_ORIGIN}${enPath}`);
      setHreflang('x-default', `${SITE_ORIGIN}${itPath}`);
    }

    // Optional Cleanup: reset to previous title (not strictly required for page routes, but clean)
    return () => {
      document.title = prevTitle;
    };
  }, [title, description, noindex]);
};
